import {copyToClipboard} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {mergeAll} from 'ramda';
import {isString} from 'ramda-adjunct';

import {catchUnhandledDataGridActions, DmsDataGrid, logError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['snippet'] as const;
const DOCUMENT_PLACEHOLDER_GRID_HEIGHT = 120;

interface DocumentPlaceholderListProps extends RequiredTestIdProps {
  documentCode: string;
}

export function DocumentPlaceholderList(props: DocumentPlaceholderListProps) {
  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {documentCode: props.documentCode}]),
    [props.documentCode]
  );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    if (!isString(rowId)) {
      return logError(`Bulk invocation of single-row action ${actionKey} is not implemented`);
    }

    return match(actionKey)
      .with('copy', () => {
        const snippet = getColumnData<string>('snippet')?.value;

        if (!isString(snippet)) {
          return logError('Missing data for document placeholder snippet');
        }

        return copyToClipboard(snippet);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <Box height={DOCUMENT_PLACEHOLDER_GRID_HEIGHT} width="100%">
      <DmsDataGrid
        gridCode="document-placeholder"
        predefinedColumns={PREDEFINED_COLUMNS}
        queryModifier={queryModifier}
        actionCallback={actionCallback}
        data-testid={suffixTestId('grid', props)}
      />
    </Box>
  );
}
